<template>
    <div class="course-table" role="group" :aria-label="$t('Courses')">
        <div class="course-row course-row-head" aria-hidden="true">
            <span></span>
            <span>{{ $t('courseName') }}</span>
            <span>{{ $t('mode') }}</span>
            <span>{{ $t('price') }}</span>
        </div>

        <label
            v-for="course in allCourses"
            :key="course.id"
            class="course-row"
            :class="{
                selected: isSelected(course.id),
                disabled: isDisabled(course.id)
            }"
        >
            <input
                type="checkbox"
                :checked="isSelected(course.id)"
                :disabled="isDisabled(course.id)"
                @change="toggleCourse(course, $event.target.checked)"
            >
            <span class="course-name">{{ course.name }}</span>
            <span class="course-mode">{{ $t('online') }}</span>
            <span class="course-price">{{ course.price }} €</span>
        </label>
    </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'

const props = defineProps({
    allCourses: { type: Array, required: true },
    courseList: { type: Array, required: true },
    maxCourses: { type: Number, required: true },
    otherSelectedCount: { type: Number, default: 0 }
})

const emit = defineEmits(['update:courseList'])
const initialized = ref(false)
const selectedCourses = ref([])

function mapCourse(course) {
    return {
        id: course.id,
        name: course.name,
        price: String(course.price ?? ''),
        semester: course.semester,
        is_online: true,
        selected: true
    }
}

function syncFromProps() {
    if (props.courseList.length) {
        selectedCourses.value = props.courseList.map(mapCourse)
        initialized.value = true
        return
    }

    if (!initialized.value && props.allCourses.length) {
        const availableSlots = Math.max(0, props.maxCourses - props.otherSelectedCount)
        selectedCourses.value = props.allCourses.slice(0, availableSlots).map(mapCourse)
        initialized.value = true
        emit('update:courseList', selectedCourses.value)
    }
}

syncFromProps()
watch(() => props.courseList, syncFromProps, { deep: true })
watch(() => props.allCourses, syncFromProps, { deep: true })

const selectedIds = computed(() => selectedCourses.value.map(course => course.id))
const totalSelected = computed(() => selectedCourses.value.length + props.otherSelectedCount)

function isSelected(courseId) {
    return selectedIds.value.includes(courseId)
}

function isDisabled(courseId) {
    return !isSelected(courseId) && totalSelected.value >= props.maxCourses
}

function toggleCourse(course, checked) {
    if (checked) {
        if (totalSelected.value >= props.maxCourses || isSelected(course.id)) return
        selectedCourses.value.push(mapCourse(course))
    } else {
        selectedCourses.value = selectedCourses.value.filter(item => item.id !== course.id)
    }

    const selectedSet = new Set(selectedCourses.value.map(item => item.id))
    selectedCourses.value = props.allCourses
        .filter(item => selectedSet.has(item.id))
        .map(mapCourse)

    emit('update:courseList', selectedCourses.value)
}
</script>

<style scoped>
.course-table {
    overflow: hidden;
    border: 1px solid var(--border);
    border-radius: 12px;
}

.course-row {
    display: grid;
    grid-template-columns: 34px minmax(0, 1fr) 90px 100px;
    gap: 12px;
    align-items: center;
    min-height: 52px;
    padding: 12px 16px;
    border-top: 1px solid #f0ebdc;
    background: var(--surface);
    cursor: pointer;
    transition: background-color .15s ease, opacity .15s ease;
}

.course-row:first-child {
    border-top: 0;
}

.course-row-head {
    min-height: 42px;
    border-top: 0;
    background: var(--soft);
    color: var(--muted);
    cursor: default;
    font-size: 11.5px;
    font-weight: 600;
    letter-spacing: .03em;
    text-transform: uppercase;
}

.course-row.selected {
    background: var(--accent-tint);
}

.course-row.disabled {
    opacity: .45;
    cursor: not-allowed;
}

.course-row input {
    width: 18px;
    height: 18px;
    margin: 0;
    accent-color: var(--accent);
    cursor: inherit;
}

.course-name {
    min-width: 0;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.45;
}

.course-price {
    text-align: right;
    white-space: nowrap;
    font-size: 14px;
    font-weight: 600;
}

.course-mode {
    justify-self: start;
    padding: 4px 10px;
    border-radius: 999px;
    background: var(--soft);
    color: #555;
    font-size: 12.5px;
    font-weight: 600;
    white-space: nowrap;
}

@media (max-width: 640px) {
    .course-row {
        grid-template-columns: 28px minmax(0, 1fr) 68px 76px;
        gap: 8px;
        padding: 11px 12px;
    }
}
</style>
